<script setup lang="ts">
import { computed } from 'vue';

// Shared card-brand glyph for the multi-card panel. Byte-identical in checkout
// (components/checkout/) and hive-ui (components/billing/) so PaymentMethodsPanel
// can import it via './CardBrandIcon.vue' in both apps. Pure/presentational.

const props = defineProps<{ brand?: string | null; type?: string | null }>();

const key = computed<'visa' | 'mastercard' | 'amex' | 'discover' | 'link' | 'generic'>(() => {
    if (props.type === 'link') return 'link';
    const b = (props.brand ?? '').toLowerCase();
    if (b.includes('visa')) return 'visa';
    if (b.includes('master')) return 'mastercard';
    if (b.includes('amex') || b.includes('american')) return 'amex';
    if (b.includes('discover')) return 'discover';
    return 'generic';
});
</script>

<template>
    <span class="card-brand-icon" :data-brand="key" role="img" :aria-label="key">
        <!-- Visa -->
        <svg v-if="key === 'visa'" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 58 41" fill="none">
            <rect x="0.5" y="1" width="57" height="39" rx="3.5" fill="white" />
            <rect x="0.5" y="1" width="57" height="39" rx="3.5" stroke="#E4E4E7" />
            <path fill-rule="evenodd" clip-rule="evenodd"
                d="M18.3387 26.9304H14.825L12.1902 16.8206C12.0651 16.3556 11.7996 15.9444 11.409 15.7507C10.4342 15.2637 9.36003 14.8762 8.18823 14.6807V14.2915H13.8485C14.6297 14.2915 15.2156 14.8762 15.3132 15.5552L16.6803 22.8477L20.1923 14.2915H23.6083L18.3387 26.9304ZM25.5613 26.9304H22.2429L24.9754 14.2915H28.2938L25.5613 26.9304ZM32.587 17.7929C32.6846 17.1121 33.2705 16.7229 33.9541 16.7229C35.0282 16.6252 36.1983 16.8206 37.1748 17.3059L37.7607 14.5847C36.7842 14.1955 35.71 14 34.7353 14C31.5145 14 29.1709 15.7507 29.1709 18.1804C29.1709 20.0288 30.831 20.9993 32.0028 21.584C33.2705 22.167 33.7588 22.5563 33.6611 23.1392C33.6611 24.0137 32.6846 24.403 31.7098 24.403C30.538 24.403 29.3662 24.1115 28.2938 23.6245L27.7079 26.3474C28.8797 26.8327 30.1474 27.0281 31.3192 27.0281C34.9306 27.1242 37.1748 25.3752 37.1748 22.75C37.1748 19.4441 32.587 19.2504 32.587 17.7929V17.7929ZM48.7882 26.9304L46.1534 14.2915H43.3233C42.7374 14.2915 42.1515 14.6807 41.9562 15.2637L37.0771 26.9304H40.4931L41.175 25.0837H45.3722L45.7628 26.9304H48.7882ZM43.8115 17.6951L44.7863 22.4585H42.0538L43.8115 17.6951Z"
                fill="#172B85" />
        </svg>
        <!-- Mastercard -->
        <svg v-else-if="key === 'mastercard'" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 58 41" fill="none">
            <rect x="0.5" y="1" width="57" height="39" rx="3.5" fill="white" />
            <rect x="0.5" y="1" width="57" height="39" rx="3.5" stroke="#E4E4E7" />
            <circle cx="22" cy="20.5" r="10" fill="#EB001B" />
            <circle cx="36" cy="20.5" r="10" fill="#F79E1B" />
            <path d="M29 13.5C31.0902 15.2571 32.4 17.7341 32.4 20.5C32.4 23.2659 31.0902 25.7429 29 27.5C26.9098 25.7429 25.6 23.2659 25.6 20.5C25.6 17.7341 26.9098 15.2571 29 13.5Z" fill="#FF5F00" />
        </svg>
        <!-- American Express -->
        <svg v-else-if="key === 'amex'" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 58 41" fill="none">
            <rect x="0.5" y="1" width="57" height="39" rx="3.5" fill="#016FD0" />
            <rect x="0.5" y="1" width="57" height="39" rx="3.5" stroke="#E4E4E7" />
            <path d="M10 16L12.5 11H15.5L18 16V11H22L23 14L24 11H28V20H24L23 17L22 20H18V15L15.5 20H12.5L10 15V20H6V11H10V16Z" fill="white" />
            <path d="M6 22H12L13 24L14 22H52V30H14L13 28L12 30H6V22Z" fill="white" />
            <text x="29" y="28" fill="#016FD0" font-size="6" font-weight="bold" font-family="Arial" text-anchor="middle">AMEX</text>
        </svg>
        <!-- Discover -->
        <svg v-else-if="key === 'discover'" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 58 41" fill="none">
            <rect x="0.5" y="1" width="57" height="39" rx="3.5" fill="white" />
            <rect x="0.5" y="1" width="57" height="39" rx="3.5" stroke="#E4E4E7" />
            <path d="M0.5 20.5H57.5V36.5C57.5 38.7091 55.7091 40.5 53.5 40.5H4.5C2.29086 40.5 0.5 38.7091 0.5 36.5V20.5Z" fill="#F48120" />
            <text x="29" y="18" fill="#000" font-size="8" font-weight="bold" font-family="Arial" text-anchor="middle">DISCOVER</text>
        </svg>
        <!-- Link -->
        <svg v-else-if="key === 'link'" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 58 41" fill="none">
            <rect x="0.5" y="1" width="57" height="39" rx="3.5" fill="#00D66F" />
            <rect x="0.5" y="1" width="57" height="39" rx="3.5" stroke="#E4E4E7" />
            <text x="29" y="25" fill="#011E0F" font-size="11" font-weight="700" font-family="Arial" text-anchor="middle">link</text>
        </svg>
        <!-- Generic / unknown -->
        <svg v-else xmlns="http://www.w3.org/2000/svg" viewBox="0 0 58 41" fill="none">
            <rect x="0.5" y="1" width="57" height="39" rx="3.5" fill="white" />
            <rect x="0.5" y="1" width="57" height="39" rx="3.5" stroke="#E4E4E7" />
            <rect x="8" y="12" width="42" height="6" rx="1" fill="#E5E5E5" />
            <rect x="8" y="22" width="20" height="4" rx="1" fill="#E5E5E5" />
            <rect x="8" y="28" width="12" height="4" rx="1" fill="#E5E5E5" />
        </svg>
    </span>
</template>

<style scoped>
.card-brand-icon {
    display: inline-flex;
    flex-shrink: 0;
    width: 38px;
    height: 27px;
}
.card-brand-icon svg {
    width: 100%;
    height: 100%;
    border-radius: 4px;
}
</style>
